import { useEffect, useRef, useState } from "react";

// THE SHAREABLE QUEST CARD (Joshua 2026-09-04: "I didn't even get a
// shareable card at the end of the quest"). Unity announces the end card
// (window "questCard" event with {name, victory, cal, segments, art}); we
// compose the 1080x1350 image right away and show a real DOM button.
// iOS only opens the share sheet inside a genuine tap, and Unity's GUI
// button + async canvas work fell outside it - a DOM click with the file
// ALREADY prepared is inside it. Hidden again on "questCardHide".

type CardData = { name: string; victory: boolean; cal: number; segments: number; art?: string; secs?: number; won?: number; slashes?: number; deep?: number; shallow?: number };

async function compose(d: CardData): Promise<Blob | null> {
  const W = 1080, H = 1350;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const g = cv.getContext("2d");
  if (!g) return null;
  g.fillStyle = "#0b1020"; g.fillRect(0, 0, W, H);
  if (d.art) {
    const art = await new Promise<HTMLImageElement | null>((res) => {
      const img = new Image();
      img.onload = () => res(img);
      img.onerror = () => res(null);
      img.src = d.art!;
    });
    if (art) {
      const s = Math.max(W / art.width, (H * 0.72) / art.height);
      g.drawImage(art, (W - art.width * s) / 2, 0, art.width * s, art.height * s);
    }
  }
  const grad = g.createLinearGradient(0, H * 0.35, 0, H);
  grad.addColorStop(0, "rgba(11,16,32,0)");
  grad.addColorStop(0.55, "rgba(11,16,32,0.9)");
  grad.addColorStop(1, "#0b1020");
  g.fillStyle = grad; g.fillRect(0, 0, W, H);
  // a great sphere behind the words when there is no art - it is the game's mark
  if (!d.art) {
    const r = W * 0.28;
    const rg = g.createRadialGradient(W / 2 - r * 0.3, H * 0.33 - r * 0.3, r * 0.1, W / 2, H * 0.33, r);
    rg.addColorStop(0, "#ff8a3d"); rg.addColorStop(1, "#7a1a0a");
    g.fillStyle = rg; g.beginPath(); g.arc(W / 2, H * 0.33, r, 0, Math.PI * 2); g.fill();
    g.fillStyle = "#fff"; g.beginPath(); g.arc(W / 2 - r * 0.32, H * 0.31, r * 0.13, 0, Math.PI * 2); g.arc(W / 2 + r * 0.32, H * 0.31, r * 0.13, 0, Math.PI * 2); g.fill();
    g.fillStyle = "#111"; g.beginPath(); g.arc(W / 2 - r * 0.3, H * 0.315, r * 0.06, 0, Math.PI * 2); g.arc(W / 2 + r * 0.34, H * 0.315, r * 0.06, 0, Math.PI * 2); g.fill();
  }
  g.textAlign = "center";
  g.fillStyle = d.victory ? "#4ade80" : "#f87171";
  g.font = "800 54px -apple-system, system-ui, sans-serif";
  g.fillText(d.victory ? "QUEST COMPLETE" : "FALLEN IN BATTLE", W / 2, H * 0.66);
  g.fillStyle = "#fbbf24";
  g.font = "800 72px -apple-system, system-ui, sans-serif";
  g.fillText(d.name.toUpperCase(), W / 2, H * 0.74, W * 0.9);
  g.fillStyle = "#e2e8f0";
  g.font = "700 44px -apple-system, system-ui, sans-serif";
  g.fillText(`${d.cal} CALORIES  ·  ${d.won ?? d.segments}/${d.segments} TRIALS WON`, W / 2, H * 0.81);
  // the tally (Joshua 2026-09-07: "calories and more stats on the quest card")
  if (d.secs != null) {
    const mm = Math.floor(d.secs / 60), ss = String(d.secs % 60).padStart(2, "0");
    const tiles: [string, string][] = [[`${mm}:${ss}`, "TIME"], [String(d.slashes ?? 0), "SLASHES"], [String(d.deep ?? 0), "DEEP SQUATS"], [String(d.shallow ?? 0), "SHALLOW"]];
    const tw = (W * 0.84) / tiles.length, x0 = W * 0.08;
    tiles.forEach(([n, l], i) => {
      g.fillStyle = "#fbbf24"; g.font = "800 46px -apple-system, system-ui, sans-serif";
      g.fillText(n, x0 + tw * (i + 0.5), H * 0.865);
      g.fillStyle = "#94a3b8"; g.font = "600 22px -apple-system, system-ui, sans-serif";
      g.fillText(l, x0 + tw * (i + 0.5), H * 0.89);
    });
  }
  g.fillStyle = "#64748b";
  g.font = "600 30px -apple-system, system-ui, sans-serif";
  g.fillText("ATTACK OF THE SPHERES  ·  SPHERE WORLD", W / 2, H * 0.925);
  g.font = "600 26px -apple-system, system-ui, sans-serif";
  g.fillText("attackofthespheres.com", W / 2, H * 0.96);
  return new Promise((res) => cv.toBlob((b) => res(b), "image/png"));
}

export default function QuestShareCard() {
  const [data, setData] = useState<CardData | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const fileRef = useRef<File | null>(null);

  useEffect(() => {
    const onCard = (e: Event) => {
      const d = (e as CustomEvent).detail as CardData;
      setData(d);
      fileRef.current = null;
      compose(d).then((blob) => { if (blob) fileRef.current = new File([blob], "quest-card.png", { type: "image/png" }); });
    };
    const onHide = () => { setData(null); setPreview(null); fileRef.current = null; };
    window.addEventListener("questCard", onCard);
    window.addEventListener("questCardHide", onHide);
    return () => { window.removeEventListener("questCard", onCard); window.removeEventListener("questCardHide", onHide); };
  }, []);

  if (!data) return null;

  const share = () => {
    const f = fileRef.current;
    if (!f) return;
    const text = (data.victory ? "I conquered " : "I fell in ") + `"${data.name}" - ${data.cal} calories! attackofthespheres.com`;
    const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
    if (nav.share && nav.canShare && nav.canShare({ files: [f] })) {
      nav.share({ files: [f], title: data.name, text }).catch(() => setPreview(URL.createObjectURL(f)));
    } else {
      setPreview(URL.createObjectURL(f));   // desktop / no share sheet: show it, long-press or drag to save
    }
  };

  return (
    <>
      <button
        onClick={share}
        style={{
          position: "fixed", left: "50%", bottom: 18, transform: "translateX(-50%)",   // under the card (polish gate: it covered FINISH)
          zIndex: 60, padding: "12px 22px", borderRadius: 999,
          background: "#fbbf24", color: "#1a1206", fontWeight: 800, fontSize: 15,
          border: "none", boxShadow: "0 6px 20px rgba(0,0,0,0.45)", letterSpacing: 0.5,
        }}
      >
        SHARE YOUR CARD
      </button>
      {preview && (
        <div onClick={() => setPreview(null)}
          style={{ position: "fixed", inset: 0, zIndex: 70, background: "rgba(0,0,0,0.85)",
                   display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10 }}>
          <img src={preview} alt="quest card" style={{ maxHeight: "80vh", maxWidth: "92vw", borderRadius: 12 }} />
          <div style={{ color: "#e2e8f0", fontSize: 13 }}>long-press or drag the card to save · tap to close</div>
        </div>
      )}
    </>
  );
}
